import { useEffect, useState } from "react";
import {
	Alert,
	Autocomplete,
	CircularProgress,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { getParamsNewTicket, getFuncionarios } from "../api";
import { getErpPrincipal } from "../../../../services/erpSistema";

// Parâmetros do ticket da automação (ação CRIAR_TICKET), com selects
// estruturados por ERP — mesmas listas e obrigatoriedades dos modais de novo
// atendimento (/interno/find/paramsNewTicket). Substitui o antigo JSON cru.
//
// value/onChange trabalham com o objeto `ticketParams` salvo na regra:
//   HUBSOFT: { tipoAtendimentoId, statusAtendimentoId, origemContatoId,
//              usuariosResponsaveis: [{ id, name }], idEtapaFluxoAtendimento? }
//   IXC:     { filialId, assuntoId, departamentoId, responsavelId,
//              prioridadeId, statusId, horarioId? }
// clienteServicoId/clienteErpId não aparecem aqui: o motor resolve pelo
// vínculo do host que disparou o alerta.

export const ERP_ATIVO = () =>
	getErpPrincipal();

export const ERPS_COM_TICKET = ["HUBSOFT", "IXC"];

// Campos obrigatórios por tipo de ERP (espelho da validação do backend).
// `erp`: tipo do ERP da regra (padrão: a variável do front).
export function validarTicketParams(params = {}, erp = ERP_ATIVO()) {
	if (erp === "HUBSOFT") {
		if (
			!params.tipoAtendimentoId ||
			!params.statusAtendimentoId ||
			!params.origemContatoId ||
			!(params.usuariosResponsaveis?.length > 0)
		) {
			return "Preencha tipo, status, origem de contato e ao menos um responsável.";
		}
		return "";
	}
	if (erp === "IXC") {
		const obrigatorios = [
			"filialId",
			"assuntoId",
			"departamentoId",
			"prioridadeId",
			"statusId",
			"responsavelId",
		];
		if (obrigatorios.some((c) => !params[c])) {
			return "Preencha filial, assunto, departamento, prioridade, status e responsável.";
		}
		return "";
	}
	return `O ERP ${erp || "não configurado"} não suporta criação automática de tickets.`;
}

// `erp`/`erpId`: ERP da regra (tipo e id) — as listas saem dele.
export default function FormTicketParams({
	value = {},
	onChange,
	erroCampos,
	erp: erpProp,
	erpId,
}) {
	const erp = erpProp || ERP_ATIVO();
	const [listas, setListas] = useState(null);
	const [carregando, setCarregando] = useState(false);
	const [erroCarga, setErroCarga] = useState("");
	// IXC: colaboradores dependem do departamento escolhido.
	const [funcionarios, setFuncionarios] = useState([]);
	const [carregandoFuncionarios, setCarregandoFuncionarios] = useState(false);

	const set = (campo, v) => onChange({ ...value, [campo]: v });

	useEffect(() => {
		if (!ERPS_COM_TICKET.includes(erp)) return;
		let ativo = true;
		(async () => {
			try {
				setCarregando(true);
				setErroCarga("");
				const dados = await getParamsNewTicket(erpId);
				if (ativo) setListas(dados || {});
			} catch (_) {
				if (ativo) setErroCarga("Falha ao carregar as listas do ERP.");
			} finally {
				if (ativo) setCarregando(false);
			}
		})();
		return () => {
			ativo = false;
		};
	}, [erp, erpId]);

	// IXC: recarrega colaboradores quando o departamento muda.
	useEffect(() => {
		if (erp !== "IXC" || !value.departamentoId) {
			setFuncionarios([]);
			return;
		}
		let ativo = true;
		(async () => {
			try {
				setCarregandoFuncionarios(true);
				const dados = await getFuncionarios(value.departamentoId, erpId);
				if (ativo) setFuncionarios(dados || []);
			} catch (_) {
				if (ativo) setFuncionarios([]);
			} finally {
				if (ativo) setCarregandoFuncionarios(false);
			}
		})();
		return () => {
			ativo = false;
		};
	}, [erp, erpId, value.departamentoId]);

	if (!ERPS_COM_TICKET.includes(erp)) {
		return (
			<Alert severity="warning">
				O ERP {erp || "atual"} não suporta criação automática de tickets — use
				a ação "Chamar API externa".
			</Alert>
		);
	}

	if (carregando) {
		return (
			<Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
				<CircularProgress size={18} />
				<Typography variant="body2" color="text.secondary">
					Carregando listas do ERP...
				</Typography>
			</Stack>
		);
	}

	if (erroCarga) {
		return <Alert severity="error">{erroCarga}</Alert>;
	}

	if (!listas) return null;

	const campoSelect = ({
		rotulo,
		opcoes,
		idKey = "id",
		labelKey,
		campo,
		obrigatorio = true,
		loading = false,
		desabilitado = false,
	}) => (
		<Autocomplete
			options={opcoes || []}
			size="small"
			fullWidth
			loading={loading}
			disabled={desabilitado}
			value={
				value[campo] != null
					? (opcoes || []).find((o) => o?.[idKey] === value[campo]) || null
					: null
			}
			isOptionEqualToValue={(opt, val) => opt?.[idKey] === val?.[idKey]}
			getOptionLabel={(o) => String(o?.[labelKey] ?? "")}
			onChange={(_, o) => set(campo, o?.[idKey] ?? null)}
			renderInput={(params) => (
				<TextField
					{...params}
					label={rotulo}
					error={!!erroCampos && obrigatorio && !value[campo]}
				/>
			)}
		/>
	);

	if (erp === "HUBSOFT") {
		return (
			<Stack spacing={2}>
				<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
					{campoSelect({
						rotulo: "Tipo de atendimento",
						opcoes: listas.tipoAtendimento,
						labelKey: "descricao",
						campo: "tipoAtendimentoId",
					})}
					{campoSelect({
						rotulo: "Status do atendimento",
						opcoes: listas.statusAtendimento,
						labelKey: "descricao",
						campo: "statusAtendimentoId",
					})}
				</Stack>
				<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
					{campoSelect({
						rotulo: "Origem do contato",
						opcoes: listas.origemContato,
						idKey: "idOrigemContato",
						labelKey: "origemContato",
						campo: "origemContatoId",
					})}
					{campoSelect({
						rotulo: "Etapa do fluxo (opcional)",
						opcoes: listas.etapasFluxoAtendimento,
						idKey: "idEtapaFluxoAtendimento",
						labelKey: "etapaFluxoAtendimento",
						campo: "idEtapaFluxoAtendimento",
						obrigatorio: false,
					})}
				</Stack>
				<Autocomplete
					multiple
					options={listas.userResponsaveis || []}
					size="small"
					value={value.usuariosResponsaveis || []}
					isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
					getOptionLabel={(o) => o?.name || ""}
					onChange={(_, arr) => set("usuariosResponsaveis", arr)}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Usuários responsáveis"
							error={!!erroCampos && !(value.usuariosResponsaveis?.length > 0)}
							helperText="Responsáveis pelo atendimento criado pela automação"
						/>
					)}
				/>
			</Stack>
		);
	}

	// IXC
	return (
		<Stack spacing={2}>
			<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
				{campoSelect({
					rotulo: "Filial",
					opcoes: listas.filiais,
					labelKey: "razao",
					campo: "filialId",
				})}
				{campoSelect({
					rotulo: "Assunto",
					opcoes: listas.assuntos,
					labelKey: "assunto",
					campo: "assuntoId",
				})}
			</Stack>
			<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
				{campoSelect({
					rotulo: "Departamento",
					opcoes: listas.departamentos,
					labelKey: "setor",
					campo: "departamentoId",
				})}
				<Autocomplete
					options={funcionarios}
					size="small"
					fullWidth
					loading={carregandoFuncionarios}
					disabled={!value.departamentoId}
					value={
						funcionarios.find((f) => f?.id === value.responsavelId) || null
					}
					isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
					getOptionLabel={(o) => o?.funcionario || ""}
					onChange={(_, o) => set("responsavelId", o?.id ?? null)}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Colaborador responsável"
							error={!!erroCampos && !value.responsavelId}
							helperText={
								value.departamentoId ? "" : "Escolha um departamento primeiro"
							}
						/>
					)}
				/>
			</Stack>
			<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
				{campoSelect({
					rotulo: "Prioridade",
					opcoes: listas.prioridades,
					labelKey: "prioridade",
					campo: "prioridadeId",
				})}
				{campoSelect({
					rotulo: "Status",
					opcoes: listas.status,
					labelKey: "status",
					campo: "statusId",
				})}
				{campoSelect({
					rotulo: "Horário (opcional)",
					opcoes: listas.horarios,
					labelKey: "horario",
					campo: "horarioId",
					obrigatorio: false,
				})}
			</Stack>
		</Stack>
	);
}
